import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../../firebase';
import { app } from '../../../../firebase';

// ─── Constantes ───────────────────────────────────────────────────────────────

const CARROCERIAS = [
  { value: 'sedan', label: 'Sedán' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'suv', label: 'SUV / Todoterreno' },
  { value: 'pickup', label: 'Pickup / Camioneta' },
  { value: 'van', label: 'Van / Minivan' },
  { value: 'coupe', label: 'Coupé' },
  { value: 'convertible', label: 'Convertible / Cabrio' },
  { value: 'station', label: 'Station Wagon' },
];

const COLORES_FAMILIA = [
  { value: 'blanco',   label: 'Blanco',   hex: '#F5F5F5' },
  { value: 'negro',    label: 'Negro',    hex: '#1A1A1A' },
  { value: 'gris',     label: 'Gris',     hex: '#8C8C8C' },
  { value: 'plata',    label: 'Plata',    hex: '#C0C0C0' },
  { value: 'rojo',     label: 'Rojo',     hex: '#CC2222' },
  { value: 'azul',     label: 'Azul',     hex: '#1E4FA3' },
  { value: 'verde',    label: 'Verde',    hex: '#2E7D32' },
  { value: 'amarillo', label: 'Amarillo', hex: '#F5C518' },
  { value: 'naranja',  label: 'Naranja',  hex: '#E86D0A' },
  { value: 'marron',   label: 'Marrón',   hex: '#6D4C41' },
  { value: 'beige',    label: 'Beige',    hex: '#D4C5A9' },
];

const FORM_INICIAL = {
  marca: '', modelo: '', yearDesde: '', yearHasta: '',
  carroceria: '', puertas: '', colorFamilia: '', colorHex: '',
  colorNombre: '', version: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pollinationsUrl(imagePrompt, seed = 42) {
  const prompt = encodeURIComponent((imagePrompt || '').slice(0, 300));
  return `https://image.pollinations.ai/prompt/${prompt}?width=512&height=384&seed=${seed}&nologo=true&model=flux`;
}

function AssetImage({ imageUrl, promptData, alt }) {
  const [estado, setEstado] = useState('loading');
  const src = imageUrl || (promptData?.imagePrompt ? pollinationsUrl(promptData.imagePrompt) : null);
  if (!src) return <div className="w-16 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-xs">Sin imagen</div>;
  return (
    <div className="w-16 h-12 rounded overflow-hidden bg-slate-100 flex-shrink-0">
      {estado === 'loading' && <div className="w-full h-full animate-pulse bg-slate-200" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setEstado('loaded')}
        onError={() => setEstado('error')}
        className={`w-full h-full object-cover ${estado === 'loaded' ? 'block' : 'hidden'}`}
      />
      {estado === 'error' && <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px]">Error</div>}
    </div>
  );
}

function VehicleImagePreview({ imagePrompt, seed = 42, alt, className = '' }) {
  const [state, setState] = useState('loading');
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const url = pollinationsUrl(imagePrompt, seed);

  useEffect(() => {
    if (state !== 'loading') { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [state]);

  return (
    <div className={`relative rounded-xl overflow-hidden bg-slate-100 ${className}`} style={{ minHeight: '180px' }}>
      {state === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
          <div className="w-7 h-7 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
          <p className="text-xs text-slate-500 text-center">
            Renderizando{elapsed > 5 ? ` (${elapsed}s)` : '…'}
          </p>
          {elapsed > 40 && (
            <p className="text-[11px] text-slate-400 text-center">Puede tardar hasta 60s la primera vez</p>
          )}
        </div>
      )}
      {state === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <p className="text-sm text-slate-400">⚠ No se pudo cargar</p>
          <button
            type="button"
            onClick={() => { setState('loading'); setElapsed(0); }}
            className="text-xs text-blue-600 hover:underline"
          >
            Reintentar
          </button>
        </div>
      )}
      <img
        src={url}
        alt={alt}
        onLoad={() => setState('loaded')}
        onError={() => setState('error')}
        className={`w-full h-full object-cover transition-opacity duration-500 ${state === 'loaded' ? 'opacity-100' : 'opacity-0'}`}
      />
      {state === 'loaded' && (
        <span className="absolute bottom-2 left-2 text-[10px] font-bold text-white bg-black/50 px-2 py-0.5 rounded-full">
          ✦ Pollinations AI
        </span>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function GestionAvataresBiblioteca() {
  const [assets, setAssets]               = useState([]);
  const [cargando, setCargando]           = useState(true);
  const [form, setForm]                   = useState(FORM_INICIAL);
  const [generando, setGenerando]         = useState(false);
  const [error, setError]                 = useState(null);
  const [exito, setExito]                 = useState(null);
  const [filtroMarca, setFiltroMarca]     = useState('');
  const [modalVer, setModalVer]           = useState(null);
  const [regenerandoId, setRegenerandoId] = useState(null); // assetId en regeneración

  // ── Cargar assets existentes ────────────────────────────────────────────────
  const cargarAssets = useCallback(async () => {
    setCargando(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'vehicle_avatar_assets'), orderBy('creadoEn', 'desc'), limit(200))
      );
      setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.esGlobal !== false));
    } catch (e) {
      console.error('Error cargando assets:', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargarAssets(); }, [cargarAssets]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const elegirColor = (c) => {
    set('colorFamilia', c.value);
    set('colorHex', c.hex);
    if (!form.colorNombre) set('colorNombre', c.label);
  };

  const puedeGenerar =
    form.marca.trim() && form.modelo.trim() && form.carroceria &&
    form.puertas && form.colorFamilia && form.yearDesde && !generando;

  // ── Generar asset ───────────────────────────────────────────────────────────
  const handleGenerar = async () => {
    if (!puedeGenerar) return;
    setGenerando(true);
    setError(null);
    setExito(null);
    try {
      const fn = httpsCallable(getFunctions(app), 'generarAssetBiblioteca', { timeout: 130000 });
      const result = await fn({
        marca:        form.marca.trim(),
        modelo:       form.modelo.trim(),
        yearDesde:    Number(form.yearDesde),
        yearHasta:    form.yearHasta ? Number(form.yearHasta) : null,
        carroceria:   form.carroceria,
        puertas:      Number(form.puertas),
        colorFamilia: form.colorFamilia,
        colorHex:     form.colorHex,
        colorNombre:  form.colorNombre.trim() || form.colorFamilia,
        version:      form.version.trim() || '',
      });

      if (result.data?.yaExistia) {
        setExito('Este modelo ya tiene un avatar precargado. Se muestra el existente.');
      } else {
        setExito('Avatar generado y guardado correctamente en la biblioteca.');
        setForm(FORM_INICIAL);
      }
      await cargarAssets();
    } catch (e) {
      setError(e.message || 'Error al generar el avatar');
    } finally {
      setGenerando(false);
    }
  };

  // ── Regenerar imagen de un asset existente ─────────────────────────────────
  const handleRegenerarImagen = async (assetId) => {
    setRegenerandoId(assetId);
    try {
      const fn = httpsCallable(getFunctions(app), 'regenerarImagenAsset', { timeout: 130000 });
      const result = await fn({ assetId });
      const newImageUrl = result.data?.imageUrl;
      // Actualizar en la lista local para reflejo inmediato
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, imageUrl: newImageUrl } : a));
      // Si el modal está abierto para este asset, actualizarlo también
      setModalVer(prev => prev?.id === assetId ? { ...prev, imageUrl: newImageUrl } : prev);
    } catch (e) {
      setError(`Error regenerando imagen: ${e.message}`);
    } finally {
      setRegenerandoId(null);
    }
  };

  // ── Filtro ──────────────────────────────────────────────────────────────────
  const assetsFiltrados = filtroMarca.trim()
    ? assets.filter(a =>
        (a.marca || '').toLowerCase().includes(filtroMarca.toLowerCase()) ||
        (a.modelo || '').toLowerCase().includes(filtroMarca.toLowerCase())
      )
    : assets;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Avatares de biblioteca</h1>
        <p className="text-slate-600 mt-1">
          Genera avatares precargados para modelos de vehículos. Estos avatares se reutilizan
          automáticamente cuando un cliente solicita uno del mismo modelo.
        </p>
      </div>

      {/* Formulario de generación */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-5">
        <h2 className="font-semibold text-slate-800 text-lg flex items-center gap-2">
          🎨 Generar nuevo avatar
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Marca */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Marca *</label>
            <input
              type="text"
              value={form.marca}
              onChange={e => set('marca', e.target.value)}
              placeholder="Ej: Toyota"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Modelo */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Modelo *</label>
            <input
              type="text"
              value={form.modelo}
              onChange={e => set('modelo', e.target.value)}
              placeholder="Ej: Corolla"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Versión */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Versión</label>
            <input
              type="text"
              value={form.version}
              onChange={e => set('version', e.target.value)}
              placeholder="Ej: 1.4 16V (opcional)"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Año desde */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Año desde *</label>
            <input
              type="number"
              value={form.yearDesde}
              onChange={e => set('yearDesde', e.target.value)}
              placeholder="Ej: 2000"
              min="1950" max="2030"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Año hasta */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Año hasta</label>
            <input
              type="number"
              value={form.yearHasta}
              onChange={e => set('yearHasta', e.target.value)}
              placeholder="Ej: 2006 (vacío = sin límite)"
              min="1950" max="2030"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Carrocería */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Carrocería *</label>
            <select
              value={form.carroceria}
              onChange={e => set('carroceria', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Seleccionar…</option>
              {CARROCERIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* Puertas */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Puertas *</label>
            <select
              value={form.puertas}
              onChange={e => set('puertas', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Seleccionar…</option>
              {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n} puertas</option>)}
            </select>
          </div>

          {/* Color nombre */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre del color</label>
            <input
              type="text"
              value={form.colorNombre}
              onChange={e => set('colorNombre', e.target.value)}
              placeholder="Ej: Blanco Perla"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Selector de color familia */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Color familia *</label>
          <div className="flex flex-wrap gap-2">
            {COLORES_FAMILIA.map(c => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => elegirColor(c)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  form.colorFamilia === c.value
                    ? 'border-blue-500 scale-110 shadow-md'
                    : 'border-slate-300 hover:border-slate-500'
                }`}
                style={{ backgroundColor: c.hex }}
              />
            ))}
          </div>
          {form.colorFamilia && (
            <p className="text-xs text-slate-500 mt-1">
              Seleccionado: <span className="font-semibold capitalize">{form.colorFamilia}</span>
            </p>
          )}
        </div>

        {/* Mensajes de feedback */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
            <span className="shrink-0">⚠️</span>
            <span>{error}</span>
          </div>
        )}
        {exito && (
          <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700 flex items-start gap-2">
            <span className="shrink-0">✅</span>
            <span>{exito}</span>
          </div>
        )}

        {/* Botón generar */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleGenerar}
            disabled={!puedeGenerar}
            className="px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {generando ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Generando…
              </>
            ) : '✨ Generar avatar'}
          </button>
          {generando && (
            <p className="text-xs text-slate-500">Claude está generando el prompt técnico…</p>
          )}
        </div>
      </div>

      {/* Listado de assets */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-800 text-lg">Biblioteca de avatares</h2>
            <p className="text-xs text-slate-500 mt-0.5">{assets.length} avatar{assets.length !== 1 ? 'es' : ''} precargados</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={filtroMarca}
              onChange={e => setFiltroMarca(e.target.value)}
              placeholder="Filtrar por marca o modelo…"
              className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
            />
            <button
              type="button"
              onClick={cargarAssets}
              className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm text-slate-600 transition-colors"
              title="Recargar"
            >
              🔄
            </button>
          </div>
        </div>

        {cargando ? (
          <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
            <span className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mr-2" />
            Cargando avatares…
          </div>
        ) : assetsFiltrados.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <p className="text-4xl mb-3">🚗</p>
            <p className="font-medium">
              {filtroMarca ? 'Sin resultados para ese filtro' : 'No hay avatares en la biblioteca'}
            </p>
            <p className="text-sm mt-1">Usa el formulario de arriba para generar el primero.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Vehículo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Preview</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Parámetros</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Generado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {assetsFiltrados.map(asset => (
                  <tr key={asset.id} className="hover:bg-slate-50 transition-colors">
                    {/* Vehículo */}
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{asset.marca} {asset.modelo}</p>
                      {asset.version && <p className="text-xs text-slate-500">{asset.version}</p>}
                    </td>

                    {/* Preview */}
                    <td className="px-4 py-3">
                      <AssetImage
                        imageUrl={asset.imageUrl}
                        promptData={asset.promptData}
                        alt={`${asset.marca} ${asset.modelo}`}
                      />
                    </td>

                    {/* Parámetros */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-block bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5 rounded-full capitalize">{asset.carroceria}</span>
                        <span className="inline-block bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5 rounded-full">{asset.puertas}p</span>
                        <span
                          className="inline-block text-[11px] px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: asset.colorHex || '#888' }}
                          title={asset.colorNombre || asset.colorFamilia}
                        >
                          {asset.colorNombre || asset.colorFamilia}
                        </span>
                        <span className="inline-block bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5 rounded-full">
                          {asset.yearDesde}{asset.yearHasta && asset.yearHasta !== asset.yearDesde ? `–${asset.yearHasta}` : ''}
                        </span>
                      </div>
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
                        asset.estado === 'activo'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {asset.estado === 'activo' ? '✓ Activo' : asset.estado || 'activo'}
                      </span>
                    </td>

                    {/* Fecha */}
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {asset.creadoEn?.toDate
                        ? asset.creadoEn.toDate().toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setModalVer(asset)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline"
                        >
                          Ver
                        </button>
                        {asset.promptData?.imagePrompt && (
                          <button
                            type="button"
                            onClick={() => handleRegenerarImagen(asset.id)}
                            disabled={regenerandoId === asset.id}
                            className="text-xs text-slate-500 hover:text-slate-700 font-medium hover:underline disabled:opacity-40 flex items-center gap-1"
                          >
                            {regenerandoId === asset.id ? (
                              <><span className="w-3 h-3 rounded-full border border-slate-400 border-t-transparent animate-spin" />Regenerando…</>
                            ) : '🔄 Imagen'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {modalVer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setModalVer(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <h3 className="font-bold text-slate-900 text-lg">
                {modalVer.marca} {modalVer.modelo}
              </h3>
              <button type="button" onClick={() => setModalVer(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">✕</button>
            </div>

            {/* Imagen grande */}
            {modalVer.imageUrl ? (
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
                <img
                  src={modalVer.imageUrl}
                  alt={`${modalVer.marca} ${modalVer.modelo}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : modalVer.promptData?.imagePrompt ? (
              <VehicleImagePreview
                imagePrompt={modalVer.promptData.imagePrompt}
                seed={42}
                alt={`${modalVer.marca} ${modalVer.modelo}`}
                className="aspect-[4/3]"
              />
            ) : null}

            {/* Datos */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Carrocería:</span> <span className="font-medium capitalize">{modalVer.carroceria}</span></div>
              <div><span className="text-slate-500">Puertas:</span> <span className="font-medium">{modalVer.puertas}</span></div>
              <div><span className="text-slate-500">Color:</span> <span className="font-medium">{modalVer.colorNombre || modalVer.colorFamilia}</span></div>
              <div><span className="text-slate-500">Años:</span> <span className="font-medium">{modalVer.yearDesde}{modalVer.yearHasta ? `–${modalVer.yearHasta}` : '+'}</span></div>
            </div>

            {/* Prompt */}
            {modalVer.promptData?.imagePrompt && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1">Prompt técnico</p>
                <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 leading-relaxed font-mono whitespace-pre-wrap">
                  {modalVer.promptData.imagePrompt}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              {modalVer.promptData?.imagePrompt && (
                <button
                  type="button"
                  onClick={() => handleRegenerarImagen(modalVer.id)}
                  disabled={regenerandoId === modalVer.id}
                  className="flex-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {regenerandoId === modalVer.id ? (
                    <><span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />Regenerando imagen…</>
                  ) : '🔄 Regenerar imagen'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setModalVer(null)}
                className="flex-1 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
